import { createTheme, alpha } from '@mui/material/styles';
import { PaletteOptions } from '@mui/material/styles/createPalette';

const GREY = {
  300: '#F1F2F4',
  400: '#FAFAFA',
  500: '#A0AEC0',
  600: '#718096',
  700: '#ABABAB',
  800: '#111827',
};

const PRIMARY = {
  lighter: '#E7F7EF',
  light: '#0CAF60',
  main: '#297041',
  dark: '#1e5230', // Derived from main
  contrastText: '#fff',
};

const SECONDARY = {
  lighter: '#FFF0E6',
  light: '#FE964A',
  main: '#EDB42E',
  dark: '#a57d20', // Derived from main
  contrastText: '#fff',
};

const WARNING = {
  lighter: '#FFF0E6',
  light: '#FD6A6A',
  main: '#FE964A',
  dark: '#ac2a3d', // Derived from main
  contrastText: '#fff',
};

const INFO = {
  lighter: '#D0F2FF',
  light: '#74CAFF',
  main: '#1890FF',
  dark: '#0C53B7',
  contrastText: '#fff',
};

const SUCCESS = {
  lighter: '#E7F7EF',
  light: '#AAF27F',
  main: '#0CAF60',
  dark: '#229A16',
  contrastText: '#fff',
};

const ERROR = {
  lighter: '#FFF0F0',
  light: '#FFA48D',
  main: '#FD6A6A',
  dark: '#B72136',
  contrastText: '#fff',
};

const palette: PaletteOptions = {
  common: { black: '#000000', white: '#FFFFFF' },
  primary: PRIMARY,
  secondary: SECONDARY,
  info: INFO,
  success: SUCCESS,
  warning: WARNING,
  error: ERROR,
  grey: {
    100: '#F1F2F4',
    200: '#FAFAFA',
    300: '#A0AEC0',
    400: '#718096',
    500: '#ABABAB',
    600: '#111827',
  },
  divider: '#EEEFF2',
  text: {
    primary: '#111827',
    secondary: '#718096',
  },
  background: {
    paper: '#FFFFFF',
    default: '#FAFAFA',
  },
  action: {
    active: GREY[600],
    hover: alpha(GREY[500], 0.08),
    selected: alpha(GREY[500], 0.16),
    disabled: alpha(GREY[500], 0.8),
    disabledBackground: alpha(GREY[500], 0.24),
    focus: alpha(GREY[500], 0.24),
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

const theme = createTheme({
  palette,
  shape: { borderRadius: 8 },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    fontWeightRegular: 400,
    fontWeightMedium: 600,
    fontWeightBold: 700,
    h1: {
      fontWeight: 700,
      lineHeight: 80 / 64,
      fontSize: '2.5rem',
      letterSpacing: 2,
    },
    h2: {
      fontWeight: 700,
      lineHeight: 64 / 48,
      fontSize: '2rem',
      letterSpacing: 1,
    },
    h3: {
      fontWeight: 700,
      lineHeight: 1.5,
      fontSize: '1.75rem',
      letterSpacing: 0.5,
    },
    h4: {
      fontWeight: 700,
      lineHeight: 1.5,
      fontSize: '1.5rem',
      letterSpacing: 0.25,
    },
    h5: {
      fontWeight: 700,
      lineHeight: 1.5,
      fontSize: '1.25rem',
    },
    h6: {
      fontWeight: 700,
      lineHeight: 28 / 18,
      fontSize: '1.125rem',
    },
    subtitle1: {
      fontWeight: 600,
      lineHeight: 1.5,
      fontSize: '1rem',
    },
    subtitle2: {
      fontWeight: 600,
      lineHeight: 22 / 14,
      fontSize: '0.875rem',
    },
    body1: {
      lineHeight: 1.5,
      fontSize: '1rem',
    },
    body2: {
      lineHeight: 22 / 14,
      fontSize: '0.875rem',
    },
    caption: {
      lineHeight: 1.5,
      fontSize: '0.75rem',
    },
    overline: {
      fontWeight: 700,
      lineHeight: 1.5,
      fontSize: '0.75rem',
      textTransform: 'uppercase',
    },
    button: {
      fontWeight: 700,
      lineHeight: 24 / 14,
      fontSize: '0.875rem',
      textTransform: 'capitalize',
    },
  },
});

export default theme;
